import React, { useState, useEffect } from 'react';
import { X, ShoppingCart, UserPlus, Package, Zap, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Category icons and colors
const CATEGORY_CONFIG = {
  recovery: { 
    icon: ShoppingCart, 
    color: 'bg-orange-500', 
    textColor: 'text-orange-400',
    bgLight: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30'
  },
  engagement: { 
    icon: UserPlus, 
    color: 'bg-green-500', 
    textColor: 'text-green-400',
    bgLight: 'bg-green-500/10',
    borderColor: 'border-green-500/30'
  },
  transactional: { 
    icon: Package, 
    color: 'bg-blue-500', 
    textColor: 'text-blue-400',
    bgLight: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30'
  },
};

export function TemplateGallery({ open, onClose, onSelectTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      if (!open) return;
      
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/v1/journeys/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(response.data.templates || []);
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Failed to load templates:', error);
        toast.error('Failed to load journey templates');
      } finally {
        setLoading(false);
      }
    };
    
    loadTemplates();
  }, [open]);

  // Filter templates
  const filteredTemplates = templates.filter(t => {
    if (selectedCategory !== 'all' && t.category !== selectedCategory) return false;
    if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Create journey from template
  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate) return;
    
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/v1/journeys/from-template/${selectedTemplate.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Journey created from template!');
      onSelectTemplate(response.data.journey);
      onClose();
    } catch (error) {
      console.error('Failed to create journey:', error);
      toast.error('Failed to create journey from template');
    } finally {
      setCreating(false);
    }
  };

  const getCategoryConfig = (category) => {
    return CATEGORY_CONFIG[category] || {
      icon: Zap,
      color: 'bg-gray-500',
      textColor: 'text-gray-400',
      bgLight: 'bg-gray-500/10',
      borderColor: 'border-gray-500/30'
    };
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-wt-surface border-wt-border max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4 border-b border-wt-border">
          <DialogTitle className="text-wt-text-primary text-xl">
            Start with a Template
          </DialogTitle>
          <p className="text-sm text-wt-text-muted mt-1">
            Choose a pre-built journey template to get started quickly
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Left Sidebar - Categories */}
            <div className="w-48 border-r border-wt-border pr-4 py-4 space-y-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-wt-gold text-black font-medium'
                    : 'text-wt-text-muted hover:bg-wt-paper hover:text-wt-text-primary'
                }`}
              >
                All Templates
              </button>
              
              {categories.map(cat => {
                const config = getCategoryConfig(cat.id);
                const Icon = config.icon;
                
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                      selectedCategory === cat.id
                        ? 'bg-wt-gold text-black font-medium'
                        : 'text-wt-text-muted hover:bg-wt-paper hover:text-wt-text-primary'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {cat.name}
                  </button>
                );
              })}
            </div>

            {/* Right Content - Templates Grid */}
            <div className="flex-1 pl-4 py-4 overflow-y-auto">
              {/* Search */}
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-wt-bg border-wt-border text-wt-text-primary mb-4"
              />

              {/* Templates Grid */}
              <div className="grid grid-cols-2 gap-4">
                {filteredTemplates.map(template => {
                  const config = getCategoryConfig(template.category);
                  const Icon = config.icon;
                  const isSelected = selectedTemplate?.id === template.id;
                  const nodeCount = template.flow_data?.nodes?.length || 0;

                  return (
                    <div
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-wt-gold bg-wt-gold/10'
                          : `border-wt-border hover:border-wt-gold/50 bg-wt-paper`
                      }`}
                    >
                      {/* Selected indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 p-1 rounded-full bg-wt-gold">
                          <Check className="h-3 w-3 text-black" />
                        </div>
                      )}

                      {/* Category Badge */}
                      <Badge className={`${config.bgLight} ${config.textColor} ${config.borderColor} border mb-3`}>
                        <Icon className="h-3 w-3 mr-1" />
                        {template.category}
                      </Badge>

                      {/* Template Name */}
                      <h3 className="font-semibold text-wt-text-primary mb-1">
                        {template.name}
                      </h3>

                      {/* Description */}
                      <p className="text-xs text-wt-text-muted mb-3 line-clamp-2">
                        {template.description}
                      </p>

                      {/* Flow Preview */}
                      <div className="flex items-center gap-2 text-xs text-wt-text-muted">
                        <span>{nodeCount} steps</span>
                        <span>•</span>
                        <span className="capitalize">{template.trigger_type?.replace(/_/g, ' ')}</span>
                      </div>

                      {/* Exit Conditions */}
                      {template.exit_conditions?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {template.exit_conditions.slice(0, 2).map((cond, idx) => (
                            <Badge key={idx} className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">
                              Exit: {cond.type?.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {filteredTemplates.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-wt-text-muted">
                  <Zap className="h-12 w-12 mb-3 opacity-30" />
                  <p className="text-sm">No templates found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="pt-4 border-t border-wt-border flex items-center justify-between">
          <p className="text-sm text-wt-text-muted">
            {selectedTemplate ? (
              <>Selected: <span className="text-wt-gold">{selectedTemplate.name}</span></>
            ) : (
              'Select a template to continue'
            )}
          </p>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-wt-border text-wt-text-muted"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFromTemplate}
              disabled={!selectedTemplate || creating}
              className="bg-wt-gold text-black hover:bg-wt-gold/90"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Use This Template'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TemplateGallery;
